import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'

import { getDepartures, GetDeparturesArgs } from '@/app/[locale]/[ferry]/yourbooking/_actions/getDepartures'
import { LoadingState } from '@/states/loadingAtoms'
import {
  SelectedDate,
  SelectedDepartures,
  SelectedOffer,
  SelectedPassengers,
  SelectedRoutes,
  SelectedVehicles,
} from '@/states/selection.interface'

import { getSession, getSessionCommercialDiscount, getUrlLocale } from '@/utils'

export const dateChanged = async (
  departureDate: string,
  legNumber: 1 | 2,
  setLoading: (loading: boolean) => void,
  setOutboundData: (data: BffInterfacesDeparture.Departure[]) => void,
  setReturnData: (data: BffInterfacesDeparture.Departure[]) => void,
  depDateHomeFallback: string | null,
  setDepDateHomeFallback: (date: string | null) => void,
  selectedRoutes: SelectedRoutes,
  selectedOffer: SelectedOffer,
  selectedVehicles: SelectedVehicles,
  selectedPassengers: SelectedPassengers,
  selectedDate: SelectedDate,
  selectedDepartures: SelectedDepartures,
  setSelectedDate: (dates: SelectedDate) => void,
  setIsLoadingState: (loadingState: LoadingState) => void
) => {
  setLoading(true)
  let response = await getDepartures({
    date: departureDate,
    ...buildDepartureArgs(
      legNumber,
      selectedRoutes,
      selectedOffer,
      selectedVehicles,
      selectedPassengers,
      legNumber === 2 ? selectedDepartures?.departureOut?.id : undefined
    ),
  })
  legNumber === 1 ? setOutboundData(response?.departures) : setReturnData(response?.departures)
  legNumber === 1
    ? setSelectedDate({ outboundDepartureDate: departureDate, returnDepartureDate: selectedDate.returnDepartureDate })
    : setSelectedDate({ outboundDepartureDate: selectedDate.outboundDepartureDate, returnDepartureDate: departureDate })
  if (
    legNumber === 1 &&
    !selectedDepartures.departureHome?.id &&
    selectedDate.returnDepartureDate &&
    departureDate > selectedDate.returnDepartureDate
  ) {
    response = await getDepartures({
      date: departureDate,
      ...buildDepartureArgs(2, selectedRoutes, selectedOffer, selectedVehicles, selectedPassengers, selectedDepartures?.departureOut?.id),
    })
    setReturnData(response?.departures)
    setSelectedDate({ outboundDepartureDate: departureDate, returnDepartureDate: departureDate })
  }
  if (legNumber === 1 && depDateHomeFallback && departureDate > depDateHomeFallback) setDepDateHomeFallback(departureDate)
  setTimeout(() => {
    setLoading(false)
    setIsLoadingState({ newDateLoading: false })
  }, 500)
}

// The sales-closed refresh sets `bypassCache` so the post-reload fetch skips the cached list. Init
// fetches outbound before return, so the flag is consumed per leg (both legs on a return trip) instead
// of cleared on the first call — otherwise a closed return sailing would be re-offered from the cache.
const consumeBypassCacheForLeg = (legNumber: 1 | 2): boolean => {
  const raw = window.sessionStorage.getItem('bypassCache')
  if (!raw) return false

  const remainingLegs = raw === 'true' ? new Set(['1', '2']) : new Set(raw.split(','))
  if (!remainingLegs.has(String(legNumber))) return false

  remainingLegs.delete(String(legNumber))
  if (remainingLegs.size === 0) window.sessionStorage.removeItem('bypassCache')
  else window.sessionStorage.setItem('bypassCache', [...remainingLegs].join(','))

  return true
}

export const buildDepartureArgs = (
  legNumber: 1 | 2,
  selectedRoutes: SelectedRoutes,
  selectedOffer: SelectedOffer,
  selectedVehicles: SelectedVehicles,
  selectedPassengers: SelectedPassengers,
  selectedOutboundId?: string
): Omit<GetDeparturesArgs, 'date'> => {
  const offercode = selectedOffer.offerCode
  const vehicleType = selectedVehicles.vehicleCode
  const trailerTypeCode = selectedVehicles.trailerCode || ''
  let route = legNumber === 1 ? 'DVCA' : 'CADV' // set default route so that flow doesn't break
  if (legNumber === 1 && selectedRoutes?.outbound) route = selectedRoutes.outbound
  if (legNumber === 2 && selectedRoutes?.homebound) route = selectedRoutes.homebound
  if (legNumber === 2 && !selectedRoutes?.homebound && selectedRoutes?.outbound)
    route = `${selectedRoutes.outbound?.slice(2, 4)}${selectedRoutes.outbound?.slice(0, 2)}`

  const numberOfAdults = selectedPassengers.adults.toString()
  const agesOfChildren = selectedPassengers.childrenAges.toString()
  const shouldBypassCache = consumeBypassCacheForLeg(legNumber)
  const commercialDiscount = getSessionCommercialDiscount()

  return {
    adults: numberOfAdults,
    agesOfChildren: agesOfChildren || undefined,
    bypassCache: shouldBypassCache,
    commercialDiscount: commercialDiscount || undefined,
    leg: legNumber.toString() as '1' | '2',
    locale: getUrlLocale(),
    offerCode: offercode || undefined,
    outboundDepartureId: selectedOutboundId,
    route,
    sessionId: getSession()?.sessionId,
    trailerTypeCode: trailerTypeCode || undefined,
    vehicleCode: vehicleType,
  }
}

export interface DepartureFunctions {
  DepartureClicked: (clickedDepartureId: string, legNumber: 1 | 2, clickedProduct: string, confirmFromModal?: boolean) => void
}
